import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/lib/config';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LogOut, Users, GraduationCap, MessageSquare, BarChart3, MessageSquareQuote } from 'lucide-react';

export default function AdminPage() {
  const { t } = useLang();
  const [token, setToken] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [aspirants, setAspirants] = useState([]);
  const [contact, setContact] = useState([]);
  const [reforms, setReforms] = useState([]);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e?.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      const [s, m, a, c, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { params: { token } }),
        axios.get(`${API}/admin/members`, { params: { token } }),
        axios.get(`${API}/admin/aspirants`, { params: { token } }),
        axios.get(`${API}/admin/contact`, { params: { token } }),
        axios.get(`${API}/admin/reforms`, { params: { token } }),
      ]);
      setStats(s.data);
      setMembers(m.data.items);
      setAspirants(a.data.items);
      setContact(c.data.items);
      setReforms(r.data.items);
      setAuthed(true);
      toast.success('Signed in as admin');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid token');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAuthed(false);
    setToken('');
    setStats(null);
    setMembers([]);
    setAspirants([]);
    setContact([]);
    setReforms([]);
  };

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-12 sm:py-14">
          <SectionKicker>{t('nav.admin')}</SectionKicker>
          <h1 className="font-display mt-3 text-2xl sm:text-3xl font-semibold text-[#0B1220]">{t('admin.title')}</h1>
          <p className="mt-3 max-w-3xl text-[#5B6B82] leading-relaxed">{t('admin.lede')}</p>
        </Container>
      </section>

      <section className="py-10">
        <Container>
          {!authed ? (
            <Card className="max-w-md border-[#D7E1EF] bg-white">
              <CardContent className="p-5">
                <form onSubmit={login} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-token" className="text-sm font-medium text-[#0B1220]">{t('admin.token_label')}</Label>
                    <Input id="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="admin-token-input" required />
                    <p className="text-xs text-[#5B6B82]">Default demo token: <code className="bg-[#F5F8FC] px-1.5 py-0.5 rounded">ngrp-admin-2026</code></p>
                  </div>
                  <Button type="submit" disabled={loading} className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="admin-token-submit-button">
                    {loading ? t('common.loading') : t('admin.enter')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
                  <StatCard icon={Users} label={t('admin.total_members')} value={stats?.members ?? 0} />
                  <StatCard icon={GraduationCap} label={t('admin.total_aspirants')} value={stats?.aspirants ?? 0} />
                  <StatCard icon={MessageSquareQuote} label="Reform submissions" value={stats?.reform_submissions ?? 0} />
                  <StatCard icon={MessageSquare} label={t('admin.total_messages')} value={stats?.contact_messages ?? 0} />
                </div>
                <Button variant="outline" onClick={logout} className="border-[#D7E1EF] text-[#062B5C]" data-testid="admin-logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> {t('admin.logout')}
                </Button>
              </div>

              <Tabs defaultValue="members" className="mt-6" data-testid="admin-submissions-tabs">
                <TabsList>
                  <TabsTrigger value="members" data-testid="admin-tab-members">{t('admin.tab_members')}</TabsTrigger>
                  <TabsTrigger value="aspirants" data-testid="admin-tab-aspirants">{t('admin.tab_aspirants')}</TabsTrigger>
                  <TabsTrigger value="reforms" data-testid="admin-tab-reforms">Reforms</TabsTrigger>
                  <TabsTrigger value="contact" data-testid="admin-tab-contact">{t('admin.tab_contact')}</TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4">
                  <SubTable
                    rows={members}
                    columns={['created_at', 'full_name', 'email', 'mobile', 'state', 'district', 'profession', 'role_type']}
                    testid="admin-submissions-table"
                  />
                </TabsContent>
                <TabsContent value="aspirants" className="mt-4">
                  <SubTable
                    rows={aspirants}
                    columns={['created_at', 'full_name', 'email', 'mobile', 'state', 'background_type', 'current_role', 'expertise_area']}
                    testid="admin-aspirants-table"
                  />
                </TabsContent>
                <TabsContent value="reforms" className="mt-4">
                  <SubTable
                    rows={reforms}
                    columns={['created_at', 'respondent_name', 'email', 'state', 'profession', 'reform_1', 'reform_2', 'reform_3', 'reform_4', 'reform_5']}
                    testid="admin-reforms-table"
                  />
                </TabsContent>
                <TabsContent value="contact" className="mt-4">
                  <SubTable
                    rows={contact}
                    columns={['created_at', 'name', 'email', 'subject']}
                    testid="admin-contact-table"
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="border-[#D7E1EF] bg-white">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-xs text-[#5B6B82]">{label}</div>
          <div className="kpi-num text-xl font-semibold text-[#0B1220]">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function SubTable({ rows, columns, testid }) {
  if (!rows?.length) {
    return <div className="rounded-xl border border-[#D7E1EF] bg-white p-8 text-center text-sm text-[#5B6B82]">No submissions yet.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-[#D7E1EF] bg-white">
      <Table data-testid={testid}>
        <TableHeader className="bg-[#F5F8FC]">
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c} className="text-[#062B5C] capitalize whitespace-nowrap">{c.replace(/_/g, ' ')}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id || row._id || row.email}>
              {columns.map((c) => (
                <TableCell key={c} className="text-sm text-[#0B1220] whitespace-nowrap">{formatCell(row[c])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatCell(v) {
  if (!v) return '—';
  if (typeof v === 'string' && /\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleString();
  }
  return String(v);
}
